// Approved navigation vocabulary (role-review prototype, DEC-A-009 / NAV-001 starting point).
// Route labels and icon paths are the prototype's design data. `milestone` marks workspaces whose
// production implementation has not been built yet — they render an honest placeholder, not a mock.
import type { MessageKey } from '@passage/i18n';
import roleCopy from './generated/role-copy.json' with { type: 'json' };

export interface NavItem {
  readonly route: string;
  readonly labelKey: MessageKey;
  readonly icon: string;
  /** null = implemented in this build. */
  readonly milestone: string | null;
  readonly modules: readonly number[];
}

const icons: Record<string, string> = {
  home: 'M3 10 12 3l9 7v11h-6v-7H9v7H3z',
  visits: 'M4 5h16v16H4z M8 2v6m8-6v6M4 10h16',
  reception: 'M3 21v-8h18v8M7 13V9a5 5 0 0 1 10 0v4M1 21h22',
  approvals: 'm5 12 4 4L19 6 M4 3h16v18H4z',
  contractors: 'M3 8h18v13H3zM8 8V3h8v5M3 13h18M10 13v3h4v-3',
  deliveries: 'm12 2 9 5v10l-9 5-9-5V7zM3 7l9 5 9-5M12 12v10M7 4l10 5',
  vehicles: 'm3 10 2-6h14l2 6v9h-3v-3H6v3H3zM3 10h18M6 13h2m8 0h2',
  credentials: 'M3 5h18v14H3zM7 9h4v4H7zM14 9h4m-4 4h4',
  safety: 'm12 2 9 4v6c0 5-9 10-9 10S3 17 3 12V6zM8 12l3 3 5-6',
  reports: 'M3 3v18h18M7 16v-5m5 5V6m5 10V9',
  communications: 'M3 4h18v13H9l-5 4v-4H3zM7 8h10m-10 5h6',
  privacy: 'M5 10h14v11H5zM8 10V6a4 4 0 0 1 8 0v4m-4 4v3',
  sites: 'M3 21V7l9-4v18M12 9h9v12M6 10h2m-2 4h2m-2 4h2m8-5h2m-2 4h2',
  policies: 'M4 5h16M4 12h16M4 19h16M8 2v6m8 1v6m-7 1v6',
  integrations: 'M8 3v5m8-5v5M6 8h12v5a6 6 0 0 1-12 0zM12 19v4',
  identity: 'M4 3h16v18H4zM8 7h8m-8 4h4m-4 4h8',
  operations: 'M2 12h4l3-8 6 16 3-8h4',
  users: 'M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10M3 22v-3a6 6 0 0 1 6-6h6a6 6 0 0 1 6 6v3',
  // Module 20 (ADR-024): the prototype's `ai` icon path, verbatim.
  ai: 'M4 4h16v13H9l-5 4zM8 8h8m-8 5h5',
  // ADR-028: the prototype's `people` icon path, verbatim.
  people: 'M16 21v-3a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v3m14-14a4 4 0 0 1 0 8m4 6v-3a4 4 0 0 0-3-4 M13 6a4 4 0 1 1-8 0 4 4 0 0 1 8 0',
  features: 'M3 3h7v7H3zm11 0h7v7h-7zM3 14h7v7H3zm11 0h7v7h-7z',
  menu: 'M3 6h18M3 12h18M3 18h18',
  logout: 'M9 3H3v18h6M8 12h14m-5-5 5 5-5 5',
  warning: 'm12 3 10 18H2zM12 9v5m0 3v1',
  plus: 'M12 4v16M4 12h16',
};

export function iconPath(name: string): string {
  return icons[name] ?? icons.features!;
}

export const NAV: Readonly<Record<string, NavItem>> = {
  home: { route: 'home', labelKey: 'nav.home', icon: 'home', milestone: null, modules: [2] },
  users: { route: 'users', labelKey: 'nav.users', icon: 'users', milestone: null, modules: [17] },
  sites: { route: 'sites', labelKey: 'nav.sites', icon: 'sites', milestone: null, modules: [12] },
  auditlog: { route: 'auditlog', labelKey: 'nav.auditlog', icon: 'reports', milestone: null, modules: [15, 17] },
  policies: { route: 'policies', labelKey: 'nav.policies', icon: 'policies', milestone: null, modules: [7] },
  visits: { route: 'visits', labelKey: 'nav.visits', icon: 'visits', milestone: null, modules: [4] },
  approvals: { route: 'approvals', labelKey: 'nav.approvals', icon: 'approvals', milestone: null, modules: [7] },
  reception: { route: 'reception', labelKey: 'nav.reception', icon: 'reception', milestone: null, modules: [6] },
  communications: { route: 'communications', labelKey: 'nav.communications', icon: 'communications', milestone: null, modules: [13] },
  deliveries: { route: 'deliveries', labelKey: 'nav.deliveries', icon: 'deliveries', milestone: null, modules: [9] },
  contractors: { route: 'contractors', labelKey: 'nav.contractors', icon: 'contractors', milestone: null, modules: [9] },
  vehicles: { route: 'vehicles', labelKey: 'nav.vehicles', icon: 'vehicles', milestone: null, modules: [10] },
  credentials: { route: 'credentials', labelKey: 'nav.credentials', icon: 'credentials', milestone: null, modules: [11] },
  integrations: { route: 'integrations', labelKey: 'nav.integrations', icon: 'integrations', milestone: null, modules: [18] },
  safety: { route: 'safety', labelKey: 'nav.safety', icon: 'safety', milestone: null, modules: [14] },
  summary: { route: 'summary', labelKey: 'nav.summary', icon: 'reports', milestone: null, modules: [15] },
  privacy: { route: 'privacy', labelKey: 'nav.privacy', icon: 'privacy', milestone: null, modules: [16] },
  // Module 08 (ADR-017): capability-derived entry (identity.review, verification.settings.manage).
  identity: { route: 'identity', labelKey: 'nav.identity', icon: 'identity', milestone: null, modules: [8] },
  operations: { route: 'operations', labelKey: 'nav.operations', icon: 'operations', milestone: null, modules: [19] },
  // PD-305 (people & records stream): capability-derived entry for screening reviewers and list managers.
  screening: { route: 'screening', labelKey: 'nav.screening', icon: 'safety', milestone: null, modules: [3] },
  // Module 20 (ADR-024): approved prototype "AI assistance" entry (INTELLIGENCE group); capability-derived.
  ai: { route: 'ai', labelKey: 'nav.ai', icon: 'ai', milestone: null, modules: [20] },
  // ADR-028 (PD-379): approved prototype "People & organisations" entry; capability-derived.
  people: { route: 'people', labelKey: 'nav.people', icon: 'people', milestone: null, modules: [3] },
};

export interface RoleCopy {
  readonly id: string;
  readonly title: string;
  readonly group: string;
  readonly heading: string;
  readonly intro: string;
  readonly boundary: string;
  readonly nav: readonly string[];
  readonly primary: readonly [string, string];
  readonly tasks: ReadonlyArray<readonly [string, string, string]>;
}

export const ROLE_COPY: Readonly<Record<string, RoleCopy>> = Object.fromEntries(
  (roleCopy.roles as unknown as RoleCopy[]).map((r) => [r.id, r]),
);

/**
 * Navigation for the experiences a user holds, in approved order, de-duplicated. `extraRoutes`
 * adds entries implied by capabilities rather than experiences — PD-018: site administrators who
 * can grant access (PD-005) get a scoped "Users & permissions" entry.
 */
export function navFor(experienceIds: readonly string[], extraRoutes: readonly string[] = []): NavItem[] {
  const seen = new Set<string>(['home']);
  const items: NavItem[] = [NAV.home!];
  for (const route of [...experienceIds.flatMap((id) => ROLE_COPY[id]?.nav ?? []), ...extraRoutes]) {
    const item = NAV[route];
    if (item && !seen.has(route)) {
      seen.add(route);
      items.push(item);
    }
  }
  return items;
}
